import _asyncToGenerator from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _classCallCheck from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/vercel/workpath0/node_modules/@babel/runtime/helpers/esm/createClass";
import _regeneratorRuntime from "/vercel/workpath0/node_modules/@babel/runtime/regenerator";

/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { keep, scalar, stack, tidy, unstack, util } from '@tensorflow/tfjs-core';
/**
 * Hashtable contains a set of tensors, which can be accessed by key.
 */

export var HashTable = /*#__PURE__*/function () {
  /**
   * Constructor of HashTable. Creates a hash table.
   *
   * @param keyDType `dtype` of the table keys.
   * @param valueDType `dtype` of the table values.
   */
  function HashTable(keyDType, valueDType) {
    _classCallCheck(this, HashTable);

    this.keyDType = keyDType;
    this.valueDType = valueDType;
    this.handle = scalar(0); // tslint:disable-next-line: no-any

    this.tensorMap = new Map();
    keep(this.handle);
  }

  _createClass(HashTable, [{
    key: "id",
    get: function get() {
      return this.handle.id;
    }
    /**
     * Dispose the tensors and handle and clear the hashtable.
     */

  }, {
    key: "clearAndClose",
    value: function clearAndClose() {
      this.tensorMap.forEach(function (value) {
        return value.dispose();
      });
      this.tensorMap.clear();
      this.handle.dispose();
    }
    /**
     * The number of items in the hash table.
     */

  }, {
    key: "size",
    value: function size() {
      return this.tensorMap.size;
    }
    /**
     * Replaces the contents of the table with the specified keys and values.
     * @param keys Keys to store in the hashtable.
     * @param values Values to store in the hashtable.
     */

  }, {
    key: "import",
    value: function () {
      var _import2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(keys, values) {
        var _this = this;

        var $keys;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                this.checkKeyAndValueTensor(keys, values); // We only store the primitive values of the keys, this allows lookup
                // to be O(1).

                _context.next = 3;
                return keys.data();

              case 3:
                $keys = _context.sent;
                // Clear the hashTable before inserting new values.
                this.tensorMap.forEach(function (value) {
                  return value.dispose();
                });
                this.tensorMap.clear();
                return _context.abrupt("return", tidy(function () {
                  var $values = unstack(values);
                  var keysLength = $keys.length;
                  var valuesLength = $values.length;
                  util.assert(keysLength === valuesLength, function () {
                    return "The number of elements doesn't match, keys has " + "".concat(keysLength, " elements, the values has ").concat(valuesLength, " ") + "elements.";
                  });

                  for (var i = 0; i < keysLength; i++) {
                    var key = $keys[i];
                    var value = $values[i];
                    keep(value);

                    _this.tensorMap.set(key, value);
                  }

                  return _this.handle;
                }));

              case 7:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      function _import(_x, _x2) {
        return _import2.apply(this, arguments);
      }

      return _import;
    }()
    /**
     * Looks up keys in a hash table, outputs the corresponding values.
     *
     * Performs batch lookups, for every element in the key tensor, `find`
     * stacks the corresponding value into the return tensor.
     *
     * If an element is not present in the table, the given `defaultValue` is
     * used.
     *
     * @param keys Keys to look up. Must have the same type as the keys of the
     *     table.
     * @param defaultValue The scalar `defaultValue` is the value output for keys
     *     not present in the table. It must also be of the same type as the
     *     table values.
     */

  }, {
    key: "find",
    value: function () {
      var _find = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(keys, defaultValue) {
        var _this2 = this;

        var $keys;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                this.checkKeyAndValueTensor(keys, defaultValue);
                _context2.next = 3;
                return keys.data();

              case 3:
                $keys = _context2.sent;
                return _context2.abrupt("return", tidy(function () {
                  var result = [];

                  for (var i = 0; i < $keys.length; i++) {
                    var key = $keys[i];

                    var value = _this2.findWithDefault(key, defaultValue);

                    result.push(value);
                  }

                  return stack(result);
                }));

              case 5:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function find(_x3, _x4) {
        return _find.apply(this, arguments);
      }

      return find;
    }() // tslint:disable-next-line: no-any

  }, {
    key: "findWithDefault",
    value: function findWithDefault(key, defaultValue) {
      var result = this.tensorMap.get(key);
      return result != null ? result : defaultValue;
    }
  }, {
    key: "checkKeyAndValueTensor",
    value: function checkKeyAndValueTensor(key, value) {
      if (key.dtype !== this.keyDType) {
        throw new Error("Expect key dtype ".concat(this.keyDType, ", but got ") + "".concat(key.dtype));
      }

      if (value.dtype !== this.valueDType) {
        throw new Error("Expect value dtype ".concat(this.valueDType, ", but got ") + "".concat(value.dtype));
      }
    }
  }]);

  return HashTable;
}();